import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import FileCard from './FileCard';
import { RootState } from '../../../../reducers';
import { toggleSelectFile, fetchFiles } from '../../librarySlice';
import { FileType } from '../../../../types';
import { RouteParams } from '../../types';
import { getFileUrl } from '../../../../lib/routingUtils';
import PendingUploads from '../../../upload/PendingUploads';
import FilesSkeleton from './FilesSkeleton';
import FilesEmpty from '../FilesEmpty';
import { useQuery } from '../../../../hooks/urlHooks';
import { useSelectMode } from '../../context/SelectModeContext';
import useDebounce from '../../../../hooks/useDebounce';
import RecoveryFileCard from '../RecoveryFileCard';
import {
  loadBackupRecordsThunk,
  setRecoveryAlertClosed,
} from '../../../settings/settingsSlice';

export const FileGridView = () => {
  const { library, upload, import: importState } = useSelector(
    (state: RootState) => state
  );
  const { activeUploads } = upload;
  const dispatch = useDispatch();
  const { teamId, folderId } = useParams<RouteParams>();
  const { searchQuery, sortBy = 'createdAt', sortOrder = 'desc' } = useQuery();
  const { files } = library;
  const { selectedFiles } = library;
  const { selectMode } = useSelectMode();
  const showCheckbox = selectMode || Object.keys(selectedFiles).length > 0;
  const { status } = files;
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadingPage, setLoadingPage] = useState<number | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);

  // Debounce the shouldLoadMore trigger to prevent rapid API calls
  const debouncedShouldLoadMore = useDebounce(shouldLoadMore, 100);

  const onFileSelect = useCallback(
    (file: FileType) => {
      dispatch(toggleSelectFile(file));
    },
    [dispatch]
  );

  const handleScroll = useCallback(() => {
    const { scrollHeight, scrollTop, clientHeight } = document.documentElement;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const threshold = window.innerHeight; // Start loading when within one viewport height

    // Update near-bottom state for early skeleton display
    setIsNearBottom(distanceFromBottom < threshold);

    if (
      distanceFromBottom < threshold &&
      files.status !== 'pending' &&
      !isLoadingMore &&
      files.pagination.total > files.data.length
    ) {
      const nextPage = files.pagination.page + 1;
      const maxPages = Math.ceil(
        files.pagination.total / files.pagination.limit
      );

      // Prevent duplicate page requests - only load if we haven't already loaded this page
      if (loadingPage === nextPage || nextPage > maxPages) {
        return;
      }

      // Trigger the debounced load instead of loading immediately
      setShouldLoadMore(true);
    } else {
      setShouldLoadMore(false);
    }
  }, [files, isLoadingMore, loadingPage]);

  // Effect to handle the actual API call when debounced trigger fires
  useEffect(() => {
    if (debouncedShouldLoadMore && !isLoadingMore) {
      const nextPage = files.pagination.page + 1;
      const maxPages = Math.ceil(
        files.pagination.total / files.pagination.limit
      );

      if (loadingPage !== nextPage && nextPage <= maxPages) {
        setIsLoadingMore(true);
        setLoadingPage(nextPage);
        dispatch(
          fetchFiles({
            teamId,
            folderId,
            page: nextPage,
            limit: files.pagination.limit, // Use the same limit as in pagination
            searchQuery,
            sortBy,
            sortOrder,
          })
        );
      }
    }
  }, [
    debouncedShouldLoadMore,
    files,
    dispatch,
    teamId,
    folderId,
    searchQuery,
    sortBy,
    sortOrder,
    isLoadingMore,
    loadingPage,
  ]);

  useEffect(() => {
    // Reset loading state when dependencies change
    setIsLoadingMore(false);
    setLoadingPage(null);
  }, [teamId, folderId, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    // Reset loading state when files fetch completes
    if (files.status !== 'pending') {
      setIsLoadingMore(false);
      setLoadingPage(null);
    }
  }, [files.status]);

  // Load backup records for recovery detection and reset alert state
  useEffect(() => {
    const loadBackupData = async () => {
      await dispatch(loadBackupRecordsThunk());
      // Reset alert closed state to ensure new recovery opportunities are shown
      dispatch(setRecoveryAlertClosed(false));
    };
    loadBackupData();
  }, [dispatch]);

  useEffect(() => {
    // Store the handler reference for cleanup
    scrollHandlerRef.current = handleScroll;

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Also remove with the ref in case the handler changed
      if (scrollHandlerRef.current) {
        window.removeEventListener('scroll', scrollHandlerRef.current);
      }
    };
  }, [handleScroll]);

  if (!teamId || !folderId) {
    return null;
  }

  if (
    files.data?.length === 0 &&
    activeUploads.length === 0 &&
    importState.status !== 'pending' &&
    status !== 'pending'
  ) {
    return <FilesEmpty />;
  }

  const showSkeleton =
    status === 'pending' ||
    isLoadingMore ||
    (isNearBottom && files.pagination.total > files.data.length);

  return (
    <Grid container spacing={2} sx={{ minHeight: '200px' }}>
      <PendingUploads view="grid" />

      {/* Recovery File Card - Shows when there are recent recoverable files */}
      <RecoveryFileCard />

      {files.data.map((file) => {
        const isSelected = !!selectedFiles[file._id];
        return (
          <Grid
            data-testid="video-grid-tile"
            item
            key={file._id}
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={2}
          >
            <FileCard
              file={file}
              isSelected={isSelected}
              showCheckbox={showCheckbox}
              onSelect={onFileSelect}
              freeUserTimeLimit={files.freeUserTimeLimit}
              fileUrl={getFileUrl({
                teamId,
                parentId: folderId,
                fileId: file._id,
                withBasePath: true,
              })}
            />
          </Grid>
        );
      })}
      {showSkeleton && <FilesSkeleton />}
    </Grid>
  );
};

export default FileGridView;
